/**
 * LinkedIn Sign-In config — super-admin pastes Client ID + Client Secret for
 * "Sign In with LinkedIn using OpenID Connect". Stored in Mongo
 * (`app_config._id = "linkedin_oauth"`) — no env vars or redeploy needed.
 */
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Linkedin, Save, CheckCircle2, XCircle, ExternalLink, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthProviderToggleRow } from "./AuthProviderToggleRow";

export const LinkedInSignInConfigCard: React.FC = () => {
  const qc = useQueryClient();
  const cfg = useQuery<{ client_id: string; client_secret_set: boolean }>({
    queryKey: ["linkedin-signin-config"],
    queryFn: async () => (await api.get("/auth/admin/linkedin-config")).data,
  });

  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    if (cfg.data?.client_id) setClientId(cfg.data.client_id);
  }, [cfg.data?.client_id]);

  const save = useMutation({
    mutationFn: async () => {
      const patch: any = { client_id: clientId.trim() };
      if (clientSecret.trim()) patch.client_secret = clientSecret.trim();
      return (await api.put("/auth/admin/linkedin-config", patch)).data;
    },
    onSuccess: () => {
      toast.success("LinkedIn Sign-In credentials saved · users can now log in with LinkedIn");
      setClientSecret("");
      qc.invalidateQueries({ queryKey: ["linkedin-signin-config"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const configured = !!cfg.data?.client_id && !!cfg.data?.client_secret_set;
  const originHint = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <div className="sg-card p-6" data-testid="linkedin-signin-config-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-sky-50 dark:bg-sky-950/40 flex items-center justify-center shrink-0">
          <Linkedin className="h-5 w-5 text-sky-700 dark:text-sky-400" />
        </div>
        <div className="flex-1">
          <div className="font-display text-lg font-bold text-[color:var(--sg-fg)]">LinkedIn Sign-In</div>
          <div className="text-xs text-[color:var(--sg-muted)] mt-0.5">
            The "Continue with LinkedIn" button on <code>/login</code> and <code>/signup</code>. Uses <b>Sign In with LinkedIn using OpenID Connect</b>.
          </div>
        </div>
        {configured ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900" data-testid="linkedin-signin-status">
            <CheckCircle2 className="h-3 w-3" /> Live
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400 border border-rose-200 dark:border-rose-900" data-testid="linkedin-signin-status">
            <XCircle className="h-3 w-3" /> Not configured
          </span>
        )}
      </div>

      <AuthProviderToggleRow provider="linkedin" label="LinkedIn Sign-In" />

      <div className="space-y-4">
        <div>
          <Label htmlFor="li-client-id" className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">
            Client ID
          </Label>
          <Input
            id="li-client-id"
            data-testid="linkedin-signin-client-id"
            placeholder="86xyz12345abcd"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="mt-1 font-mono text-xs"
          />
        </div>

        <div>
          <Label htmlFor="li-client-secret" className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">
            Client Secret {cfg.data?.client_secret_set && <span className="text-[10px] font-normal ml-1 text-emerald-600 dark:text-emerald-400">· saved (paste to replace)</span>}
          </Label>
          <div className="relative mt-1">
            <Input
              id="li-client-secret"
              data-testid="linkedin-signin-client-secret"
              type={showSecret ? "text" : "password"}
              placeholder={cfg.data?.client_secret_set ? "•••••••••••••••••••••••" : "WPL_AP1.abc…"}
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              className="font-mono text-xs pr-16"
            />
            <button
              type="button"
              onClick={() => setShowSecret((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-semibold uppercase tracking-wider text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)]"
            >
              {showSecret ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        <Button
          onClick={() => save.mutate()}
          disabled={save.isPending || !clientId.trim()}
          data-testid="linkedin-signin-save-btn"
          className="bg-[color:var(--sg-fg)] text-white hover:bg-black dark:bg-white dark:text-[color:var(--sg-fg)] dark:hover:bg-slate-200"
        >
          {save.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
          Save credentials
        </Button>
      </div>

      <div className="mt-5 pt-5 border-t border-[color:var(--sg-border)]">
        <div className="text-xs font-semibold text-[color:var(--sg-fg)] mb-2">LinkedIn Developer Portal setup</div>
        <ol className="text-xs text-[color:var(--sg-muted)] space-y-1.5 list-decimal pl-5">
          <li>
            Open <a href="https://www.linkedin.com/developers/apps" target="_blank" rel="noopener noreferrer" className="text-[color:var(--sg-primary)] hover:underline inline-flex items-center gap-1">LinkedIn Developers → My apps <ExternalLink className="h-3 w-3" /></a>, create an app (tie it to your company page).
          </li>
          <li>Under <b>Products</b>, request <b>Sign In with LinkedIn using OpenID Connect</b> (auto-approved).</li>
          <li>Under <b>Auth</b>, copy the <b>Client ID</b> and <b>Primary Client Secret</b> and paste them above.</li>
          <li>
            Under <b>Auth → OAuth 2.0 settings → Authorized redirect URLs</b>, add all three:
            <div className="mt-1.5 space-y-1">
              <code className="block px-2 py-1 rounded bg-[color:var(--sg-muted-bg)] text-[11px]">{originHint}/auth/linkedin/callback</code>
              <code className="block px-2 py-1 rounded bg-[color:var(--sg-muted-bg)] text-[11px]">https://socibaba.com/auth/linkedin/callback</code>
              <code className="block px-2 py-1 rounded bg-[color:var(--sg-muted-bg)] text-[11px]">https://www.socibaba.com/auth/linkedin/callback</code>
            </div>
          </li>
          <li>Requested scopes: <code>openid</code>, <code>profile</code>, <code>email</code>.</li>
          <li>Save above. Reload <code>/login</code> — the "Continue with LinkedIn" button will appear.</li>
        </ol>
      </div>
    </div>
  );
};

export default LinkedInSignInConfigCard;
